import React, { useState } from 'react';
import { AvButtonGroup, AvMessagePopover, ButtonOption } from '@fabric-ui/components';
import { Checkmark, Forward } from '@fabric-ui/icons';
import { FailFull } from '@fabric-ui/icons/colorful';
import { dotStyle, flex } from '@fabric-ui/theme';
import { emptyArray, noop } from '@fabric-ui/utils';
import { alpha, Box, Button, LinearProgress, Step, StepLabel, Stepper, styled, SxProps } from '@mui/material';

export type StepType = {
  name?: string;
  hideBack?: boolean;
  hideForward?: boolean;
  nextText?: string;
  actionProps?: {
    label: string;
    options: ButtonOption[];
  };
};

export interface AvStepperProps {
  steps?: StepType[];
  onNext?: (event?, value?) => void;
  onBack?: () => void;
  onCancel?: () => void;
  cancelText?: string;
  isLastStep?: boolean;
  activeStep?: number;
  isLoading?: boolean;
  secondaryAction?: {
    func: (v1?, v2?) => void;
    label: string;
    disabled?: boolean;
    funcProps?: Record<string, any>;
  };
  sx?: SxProps;
  disableNext?: boolean;
  sideActionButton?: {
    name: string;
    action: () => void;
    icon: React.ReactElement;
  };
  size?: 'xSmall' | 'small';
  disabledMessage?: string | React.JSX.Element;
  disableIcon?: React.JSX.Element;
}

export const AvStepper: React.FC<AvStepperProps> = ({
  steps = emptyArray,
  onNext = noop,
  onBack = noop,
  onCancel,
  cancelText = 'Cancel',
  isLastStep = false,
  activeStep = 0,
  isLoading = false,
  secondaryAction,
  sx,
  disableNext = false,
  sideActionButton,
  size,
  disabledMessage,
  disableIcon = <FailFull />,
}) => (
  <>
    {isLoading && <LinearProgress />}
    <Box sx={wrapperStyle(sx, size)}>
      {sideActionButton && (
        <Button sx={{ mx: 1 }} size={size} variant="outlined" onClick={sideActionButton.action}>
          {sideActionButton.icon} {sideActionButton.name}
        </Button>
      )}
      {steps.length <= 1 || steps[activeStep].hideBack ? undefined : (
        <Button size={size} variant="outlined" onClick={onBack} disabled={activeStep === 0}>
          <Forward style={{ transform: 'rotate(180deg)' }} />
          Back
        </Button>
      )}
      {steps.length > 1 ? (
        <Stepper activeStep={activeStep} sx={{ width: '500px', margin: 'auto' }}>
          {steps.map(({ name }) => (
            <Step key={name}>
              <StepLabel StepIconComponent={AvStepIcon}>{name}</StepLabel>
            </Step>
          ))}
        </Stepper>
      ) : (
        <div />
      )}
      <Box sx={rightButtonsStyle}>
        {!steps?.[activeStep]?.hideBack && onCancel && (
          <Button onClick={onCancel} sx={{ px: 2 }}>
            {cancelText}
          </Button>
        )}
        {secondaryAction && (
          <Button
            aria-label={secondaryAction.label}
            size={size}
            disabled={isLoading || secondaryAction.disabled}
            variant="outlined"
            onClick={event => secondaryAction.func(secondaryAction?.funcProps, event)}>
            {secondaryAction.label || 'Test'}
          </Button>
        )}
        {steps.length > 0 && !steps[activeStep]?.actionProps && (
          <Button size={size} disabled={disableNext || isLoading} variant="contained" onClick={() => onNext()}>
            {steps[activeStep]?.nextText ? steps[activeStep]?.nextText : isLastStep ? 'Finish' : 'Next'}
            {!isLastStep && steps.length > 1 && !steps[activeStep]?.hideForward && <Forward />}
          </Button>
        )}
        {steps[activeStep]?.actionProps && (
          <AvButtonGroup
            // @ts-ignore
            variant="contained"
            size="small"
            options={steps[activeStep].actionProps!.options}
            shouldShowGroups
            disabled={disableNext || isLoading}
          />
        )}

        {disableNext && disabledMessage && <AvMessagePopover icon={disableIcon} message={disabledMessage} placement="top" />}
      </Box>
    </Box>
  </>
);

const wrapperStyle = (sx, size) => theme => ({
  ...flex.justifyBetween,
  width: '100%',
  background: theme.palette.colors.neutrals[100],
  paddingX: 3,
  paddingY: size === 'small' || size === 'xSmall' ? 1 : 2,
  boxShadow: '0px 1px 26px 0px rgba(0, 0, 0, 0.08)',
  zIndex: theme.zIndex.drawer,
  ...sx,
});

const rightButtonsStyle = {
  ...flex.row,
  gap: 1,
};

const AvStepIconRoot = styled('div')(({ theme, state }: any) => ({
  color: theme.palette.colors.neutrals[150],
  ...flex.center,
  ...dotStyle(state.active ? alpha(theme.palette.colors.primary[350], 0.3) : 'transparent', 26),
  ...(state.active && {
    color: theme.palette.primary.main,
  }),
  '& .AvStepIcon-completedIcon': {
    color: theme.palette.primary.main,
    fontSize: 18,
  },
  '.inner': {
    ...dotStyle(state.active || state.completed ? theme.palette.primary.main : theme.palette.colors.neutrals[500], 16),
    ...flex.center,
    svg: {
      width: '85%',
    },
  },
}));

const AvStepIcon = ({ active, completed }: { active?: boolean; completed?: boolean }) => (
  // @ts-ignore
  <AvStepIconRoot state={{ active, completed }}>
    <div className="inner">{completed && <Checkmark />}</div>
  </AvStepIconRoot>
);

export const useStepper = ({
  steps = [],
  initialStep = 0,
  validateOnNext,
  onBack,
}: {
  steps: StepType[];
  initialStep: number;
  validateOnNext?: () => any;
  onBack?: () => any;
}) => {
  const [activeStep, setActiveStep] = useState(initialStep);
  const handleNext = async (shouldValidate = true) => {
    if (!shouldValidate || !validateOnNext || (await validateOnNext())) {
      setActiveStep(prevActiveStep => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    }
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const isLastStep = steps[activeStep].name === steps.at(-1)?.name;
  return { handleNext, handleBack, activeStep, setActiveStep, isLastStep };
};
